import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { fetchTags, bulkAddTags } from '@/lib/api';
import { cn } from '@/lib/utils';

interface BulkTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateIds: string[];
  onSuccess: () => void;
}

export function BulkTagDialog({ open, onOpenChange, candidateIds, onSuccess }: BulkTagDialogProps) {
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['tags'],
    queryFn: fetchTags,
    enabled: open,
  });

  const mutation = useMutation({
    mutationFn: () => bulkAddTags(candidateIds, selectedTagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['candidateTags'] });
      setSelectedTagIds([]);
      setError(null);
      onOpenChange(false);
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to add tags');
    },
  });

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSubmit = () => {
    if (selectedTagIds.length === 0) {
      setError('Please select at least one tag');
      return;
    }
    setError(null);
    mutation.mutate();
  };

  const handleClose = (value: boolean) => {
    if (!mutation.isPending) {
      setSelectedTagIds([]);
      setError(null);
      onOpenChange(value);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Tags</DialogTitle>
          <DialogDescription>
            Select tags to add to {candidateIds.length} candidate{candidateIds.length !== 1 ? 's' : ''}.
            Tags already assigned to a candidate will be skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {isLoadingTags ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
            </div>
          ) : tags.length === 0 ? (
            <p className="text-sm text-text-tertiary text-center py-8">
              No tags available. Create tags in Settings first.
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1 rounded-md border border-white/10 bg-bg-secondary p-2">
              {tags.map((tag) => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      isSelected
                        ? 'bg-emerald-600/20 text-text-primary'
                        : 'hover:bg-white/5 text-text-secondary'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded border transition-colors',
                        isSelected
                          ? 'bg-emerald-600 border-emerald-600'
                          : 'border-white/20 bg-transparent'
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span
                      className="h-3 w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="truncate">{tag.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {selectedTagIds.length > 0 && (
            <p className="text-xs text-text-tertiary">
              {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedTagIds.length === 0 || mutation.isPending}
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding Tags...
              </>
            ) : (
              `Add ${selectedTagIds.length > 0 ? selectedTagIds.length : ''} Tag${selectedTagIds.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
